import { z } from 'zod'

/**
 * SEC-FR-001 / SEC-FR-002 form schemas.
 *
 * Client-side validation here is UX only; the server remains authoritative. The
 * value is that a user learns about a bad work email before submitting a
 * four-step signup, not that we trust the browser.
 */

/** Free-mail providers are rejected: InsightAI is sold to organisations. */
export const FREE_EMAIL_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.in',
  'hotmail.com',
  'outlook.com',
  'live.com',
  'aol.com',
  'icloud.com',
  'proton.me',
  'protonmail.com',
  'rediffmail.com',
  'zoho.com',
  'mail.com',
  'gmx.com',
  'yandex.com',
]

export function isFreeEmailDomain(email: string) {
  const domain = email.split('@')[1]?.toLowerCase().trim()
  return Boolean(domain && FREE_EMAIL_DOMAINS.includes(domain))
}

const emailField = z
  .string()
  .trim()
  .min(1, 'Enter your email address')
  .email('Enter a valid email address')

/** M00-15 — login. Deliberately lax on password rules: the IdP enforces policy. */
export const loginSchema = z.object({
  email: emailField,
  password: z.string().min(1, 'Enter your password'),
  rememberMe: z.boolean().default(false),
})
export type LoginInput = z.infer<typeof loginSchema>

/**
 * M00-18 — instant work-email validation.
 *
 * Split from the full signup schema so step 1 can validate on blur without
 * dragging the later steps' requirements into the error state.
 */
export const workEmailSchema = z.object({
  email: emailField.refine((value) => !isFreeEmailDomain(value), {
    message: 'Use your work email address — personal domains are not supported',
  }),
})
export type WorkEmailInput = z.infer<typeof workEmailSchema>

export const COMPANY_SIZES = [
  { value: 'SMB_1_50', label: '1–50 employees' },
  { value: 'SMB_51_200', label: '51–200 employees' },
  { value: 'MID_201_1000', label: '201–1,000 employees' },
  { value: 'ENTERPRISE_1000_PLUS', label: 'More than 1,000 employees' },
] as const

export const PRIMARY_USE_CASES = [
  { value: 'BRAND_HEALTH', label: 'Brand health & tracking' },
  { value: 'CONCEPT_TESTING', label: 'Concept & product testing' },
  { value: 'MARKET_ENTRY', label: 'Market entry & sizing' },
  { value: 'CUSTOMER_EXPERIENCE', label: 'Customer experience' },
  { value: 'CATEGORY_UNDERSTANDING', label: 'Category & shopper understanding' },
  { value: 'OTHER', label: 'Something else' },
] as const

/**
 * Password policy. 12 characters with mixed classes is stricter than the OWASP
 * floor and matches what Keycloak will be configured to enforce — a client-side
 * rule looser than the server's produces a confusing post-submit rejection.
 */
export const passwordSchema = z
  .string()
  .min(12, 'Use at least 12 characters')
  .max(128, 'That is longer than 128 characters')
  .refine((v) => /[a-z]/.test(v), 'Include a lowercase letter')
  .refine((v) => /[A-Z]/.test(v), 'Include an uppercase letter')
  .refine((v) => /\d/.test(v), 'Include a number')
  .refine((v) => /[^A-Za-z0-9]/.test(v), 'Include a symbol')

/** M00-17 — the four signup steps, each independently validatable. */
export const signupStepSchemas = {
  email: workEmailSchema,
  company: z.object({
    companySize: z.enum(COMPANY_SIZES.map((c) => c.value) as [string, ...string[]], {
      required_error: 'Choose a company size',
    }),
    fullName: z.string().trim().min(2, 'Enter your name').max(120),
  }),
  useCase: z.object({
    primaryUseCase: z.enum(PRIMARY_USE_CASES.map((u) => u.value) as [string, ...string[]], {
      required_error: 'Choose what you are researching',
    }),
  }),
  password: z
    .object({
      password: passwordSchema,
      confirmPassword: z.string(),
      acceptTerms: z.literal(true, {
        errorMap: () => ({ message: 'Accept the terms to continue' }),
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    }),
} as const

export const signupSchema = z.object({
  email: workEmailSchema.shape.email,
  fullName: z.string().trim().min(2).max(120),
  companySize: z.enum(COMPANY_SIZES.map((c) => c.value) as [string, ...string[]]),
  primaryUseCase: z.enum(PRIMARY_USE_CASES.map((u) => u.value) as [string, ...string[]]),
  password: passwordSchema,
  acceptTerms: z.literal(true),
  marketingOptIn: z.boolean().default(false),
})
export type SignupInput = z.infer<typeof signupSchema>

export const forgotPasswordSchema = z.object({ email: emailField })
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>

export const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>

/** Feeds the strength meter. Ordinal, not a security claim. */
export function passwordStrength(password: string): {
  score: 0 | 1 | 2 | 3 | 4
  label: string
  met: { length: boolean; lower: boolean; upper: boolean; digit: boolean; symbol: boolean }
} {
  const met = {
    length: password.length >= 12,
    lower: /[a-z]/.test(password),
    upper: /[A-Z]/.test(password),
    digit: /\d/.test(password),
    symbol: /[^A-Za-z0-9]/.test(password),
  }
  const passed = Object.values(met).filter(Boolean).length
  const score = Math.max(0, Math.min(4, passed - 1)) as 0 | 1 | 2 | 3 | 4
  const labels = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong']
  return { score, label: labels[score]!, met }
}
